import { createTheme } from '@mui/material/styles';

// Define the light theme
export const createLightTheme = (locale: any) => createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#888899',
    },
    secondary: {
      main: '#ff4081', // Pink color
    },
    background: {
      paper: '#f8f9fa', // Slightly darker light background
    },
    text: {
      primary: '#000000', // Main text color
      secondary: '#343a40', // Secondary text color
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    // Customize other typography as needed
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          color: '#000000',
          transition: 'background-color 0.3s, color 0.3s',
        },
        '.App': {
          display: 'grid',
          gridTemplateAreas: `
            "menu content"
          `,
          gridTemplateRows: '1fr',
          gridTemplateColumns: '250px 1fr', // Adjust the width of the menu as needed
          height: '100vh',
          backgroundColor: '#eeeeee', // Ensure the app background adapts to the theme
          color: 'inherit', // Ensure the text color adapts to the theme
        },
        '.Menu': {
          color: '#101010',
          gridArea: 'menu',
          height: '100%',
          fontFamily: 'Arial, sans-serif',
          backgroundColor: '#f8f9fa',
          padding: '20px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          borderRight: '5px solid rgb(196, 196, 196)',
          minWidth: '270px',
        },
        '.ContentArea': {
          gridArea: 'content',
          height: '100%',
          padding: '20px',
          color: 'inherit',
        },
        '.Header, .Page': {
          backgroundColor: 'inherit',
          color: 'inherit',
        },
        '.MenuGroup': {
          marginBottom: '0px',
          '& h3': {
            fontSize: '1.2em',
            color: '#343a40',
            borderBottom: '2px solid #dee2e6',
            paddingBottom: '0px',
            marginBottom: '0px',
          },
          '& ul': {
            listStyle: 'none',
            padding: '0',
            margin: '0',
            '& li': {
              margin: '8px 8px',
              '& a': {
                textDecoration: 'none',
                color: '#303030',
                fontSize: '1em',
                transition: 'color 0.3s',
                '&:hover': {
                  color: '#0056b3',
                },
              },
            },
          },
        },
        '.Spacer': {
          flexGrow: 1,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: '#66bb6a', // Slightly green when enabled
            '& + .MuiSwitch-track': {
              backgroundColor: '#66bb6a', // Slightly green track when enabled
            },
          },
        },
        track: {
          backgroundColor: '#ef5350', // Slightly red when disabled
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: '#000000', // Ensure text is visible
        },
        selectIcon: {
          color: '#000000', // Ensure select icon is visible
        },
        select: {
          backgroundColor: '#f0f0f0', // Grey background for the page size selector
        },
      },
    },
    // Override other components as needed
  },
}, locale);

// Define the dark theme
export const createDarkTheme = (locale: any) => createTheme({
  palette: {
    mode: 'dark',
    primary: {
      // main: '#00bca4', // Cyan color
      main: '#dddddd', // Cyan color
    },
    secondary: {
      main: '#ff4081', // Pink color
    },
    background: {
      default: '#131722', // Dark background
      paper: '#121523', // Slightly lighter dark background
      // paper: '#151825', // Slightly lighter dark background
    },
    text: {
      primary: '#f8f9fa', // Main text color
      secondary: '#adb5bd', // Secondary text color
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    // Customize other typography as needed
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          color: '#8d9da',
          transition: 'background-color 0.3s, color 0.3s',
        },
        '.App': {
          display: 'grid',
          gridTemplateAreas: `
            "menu content"
          `,
          gridTemplateRows: '1fr',
          gridTemplateColumns: '250px 1fr', // Adjust the width of the menu as needed
          height: '100vh',
          backgroundColor: 'inherit', // Ensure the app background adapts to the theme
          color: 'inherit', // Ensure the text color adapts to the theme
        },
        '.Menu': {
          color: '#c8c9da',
          gridArea: 'menu',
          height: '100%',
          fontFamily: 'Arial, sans-serif',
          backgroundColor: '#131722',
          padding: '20px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          borderRight: '5px solid rgb(69, 71, 74)',
          minWidth: '270px',
        },
        '.ContentArea': {
          gridArea: 'content',
          height: '100%',
          padding: '20px',
          color: 'inherit',
        },
        '.Header, .Page': {
          backgroundColor: 'inherit',
          color: '#c8c9da',
        },
        '.MenuGroup': {
          marginBottom: '0px',
          '& h3': {
            fontSize: '1.2em',
            color: '#f8f9fa',
            borderBottom: '2px solid #6c757d',
            paddingBottom: '0px',
            marginBottom: '0px',
          },
          '& ul': {
            listStyle: 'none',
            padding: '0',
            margin: '0',
            '& li': {
              margin: '8px 8px',
              '& a': {
                textDecoration: 'none',
                color: '#f8f9fa',
                fontSize: '1em',
                transition: 'color 0.3s',
                '&:hover': {
                  color: '#adb5bd',
                },
              },
            },
          },
        },
        '.Spacer': {
          flexGrow: 1,
        },
        '.FileInfoSwitch': {
          marginTop: '20px',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiSvgIcon-root': {
            verticalAlign: 'middle',
            margin: '0 8px',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#f8f9fa', // Light background for inputs
          color: '#000000', // Dark text color for inputs
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderColor: '#6c757d', // Border color for inputs
          },
          '&:hover fieldset': {
            borderColor: '#adb5bd', // Border color on hover
          },
          '&.Mui-focused fieldset': {
            borderColor: '#f8f9fa', // Border color when focused
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: '#f8f9fa', // Ensure text is visible
        },
        selectIcon: {
          color: '#f8f9fa', // Ensure select icon is visible
        },
        select: {
          backgroundColor: '#252530', // Grey background for the page size selector
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: '#a4a4a4', // Ensure the chevron icon is visible in dark mode
          // color: '#f8f9fa', // Ensure the chevron icon is visible in dark mode
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: '#a4a4a4', // Ensure the icon color is visible in dark mode
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: '#a4a4a4', // Ensure the icon color is visible in dark mode
        },
      },
    },
    // Override other components as needed
  },
}, locale);
